import { Transform, Type } from 'class-transformer';
import {
  ArrayMaxSize,
  ArrayNotEmpty,
  ArrayUnique,
  IsArray,
  IsBoolean,
  IsDefined,
  IsIn,
  IsInt,
  IsNotEmpty,
  IsOptional,
  IsString,
  Max,
  MaxLength,
  Min,
  ValidateNested,
} from 'class-validator';
import {
  DEADLINE_ASSIGNEE_ID_MAX_LENGTH,
  DEADLINE_DOMAIN_VALUES,
  DEADLINE_TITLE_MAX_LENGTH,
  type DeadlineDomainValue,
  MAX_RENEWAL_VALIDITY_MONTHS,
} from './deadline.constants';
import { DEADLINE_UPLOAD_TOKEN_MAX_LENGTH } from './deadline-ai.constants';
import {
  CONSULTANT_BULK_MAX_TENANTS,
  CONSULTANT_CLAIM_MAX_DEADLINES,
} from './deadline-management.constants';
import { DeadlineWarningDaysDto } from './deadline-warning-days.dto';
import { IsIsoDay } from './is-iso-day.decorator';
import { RenewDeadlineDto } from './renew-deadline.dto';
import { UpdateDeadlineDto } from './update-deadline.dto';

/** Gli id delle azioni dell'assistente sono UUID: il limite tiene fuori soltanto l'input abusivo. */
const ASSISTANT_ACTION_ID_MAX_LENGTH = 100;

/**
 * Cosa il consulente inserisce (spec §11.1): `dueAt` + `validityMonths` = ricorrente; `dueAt` = unica;
 * `validityMonths` da sola = ricorrente senza data; niente = nessuna scadenza. `issuedAt` +
 * `validityMonths` (mai insieme a `dueAt`) resta per le PWA in cache e calcola la data.
 */
export class ConsultantCreateDeadlineDto extends DeadlineWarningDaysDto {
  @IsOptional()
  @IsString()
  documentTypeId?: string;

  /** Solo tipo "Altro" o riga senza tipo. */
  @IsOptional()
  @IsString()
  @MaxLength(DEADLINE_TITLE_MAX_LENGTH)
  title?: string;

  /** Usato solo per le righe senza tipo di catalogo; con un tipo vince il dominio del tipo. */
  @IsOptional()
  @IsIn(DEADLINE_DOMAIN_VALUES)
  domain?: DeadlineDomainValue;

  @IsOptional()
  @IsIsoDay()
  dueAt?: string;

  @IsOptional()
  @IsIsoDay()
  issuedAt?: string;

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  @Max(MAX_RENEWAL_VALIDITY_MONTHS)
  validityMonths?: number;

  /**
   * Chi carica il documento e i rinnovi: `true` il cliente, `false` (default) il consulente, che
   * allora allega subito il documento (`uploadToken`) e sceglie un solo cliente (400 altrimenti).
   */
  @IsOptional()
  @IsBoolean()
  clientCanUpload?: boolean;

  /** "Richiedi la firma dell'azienda": ogni file della riga attende la firma di un membro del cliente. Default spento. */
  @IsOptional()
  @IsBoolean()
  requestClientSignature?: boolean;

  /** Solo per i tipi `isPersonal`: membro attivo del cliente (422 altrimenti). Il bulk multi-cliente lo rifiuta (400). */
  @IsOptional()
  @IsString()
  @MaxLength(DEADLINE_ASSIGNEE_ID_MAX_LENGTH)
  assigneeUserId?: string;

  /** Documento letto in anteprima per quel cliente: vale solo con un cliente (il bulk multi-cliente risponde 400). */
  @IsOptional()
  @IsString()
  @MaxLength(DEADLINE_UPLOAD_TOKEN_MAX_LENGTH)
  uploadToken?: string;
}

export class ConsultantCreateSingleDeadlineDto extends ConsultantCreateDeadlineDto {
  @IsString()
  @IsNotEmpty()
  tenantId!: string;
}

export class ConsultantBulkCreateDeadlinesDto {
  @IsArray()
  @ArrayNotEmpty()
  @ArrayMaxSize(CONSULTANT_BULK_MAX_TENANTS)
  @ArrayUnique()
  @IsString({ each: true })
  tenantIds!: string[];

  @IsDefined()
  @ValidateNested()
  @Type(() => ConsultantCreateDeadlineDto)
  payload!: ConsultantCreateDeadlineDto;

  /**
   * Solo dal dialog aperto con "Modifica e crea" dalla card dell'assistente: la card
   * `CREATE_CONSULTANT_DEADLINES` che questo salvataggio chiude (404 se non è del consulente,
   * 409 se è già stata creata). Le righe ne portano l'id: un "Crea" successivo non aggiunge nulla.
   */
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  @MaxLength(ASSISTANT_ACTION_ID_MAX_LENGTH)
  assistantActionId?: string;
}

/**
 * Il cliente del documento: campo di testo del multipart di `POST consultant/deadlines/ai-preview` e
 * `.../upload-staging` (accanto al `file`), query di `GET consultant/deadlines/ai-preview/quota`.
 */
export class ConsultantDeadlineAiPreviewDto {
  @IsString()
  @IsNotEmpty()
  tenantId!: string;
}

export class ConsultantClaimDeadlinesDto {
  @IsArray()
  @ArrayNotEmpty()
  @ArrayMaxSize(CONSULTANT_CLAIM_MAX_DEADLINES)
  @ArrayUnique()
  @IsString({ each: true })
  deadlineIds!: string[];
}

export class ConsultantUpdateDeadlineDto extends UpdateDeadlineDto {
  /** Solo per le righe gestite dal consulente; sulle righe `CLIENT` il service risponde 400. */
  @IsOptional()
  @IsBoolean()
  clientCanUpload?: boolean;

  /** Come `clientCanUpload`: solo righe gestite dal consulente. Vale per i file caricati da qui in poi. */
  @IsOptional()
  @IsBoolean()
  requestClientSignature?: boolean;
}

/** Rinnovo (o "Carica il documento") del consulente: multipart, i booleani arrivano come testo. */
export class ConsultantRenewDeadlineDto extends RenewDeadlineDto {
  /** Solo per questo file: assente = la scelta della riga. Ammesso solo a chi gestisce la riga. */
  @IsOptional()
  @Transform(({ value }) => (value === 'true' ? true : value === 'false' ? false : value))
  @IsBoolean()
  requestClientSignature?: boolean;
}
